import type { MemberOut, TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input, MultiSelect, Select, Switch, Textarea, toast } from "@sagot/ui";
import { useEffect, useId, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { zodResolver } from "../lib/zod-resolver";
import { DeleteTeamDialog } from "./delete-team-dialog";
import { parseTopics } from "./team-model";
import { useSaveTeam } from "./use-team";

const NAME_MAX = 120;
const DESCRIPTION_MAX = 2000;
const EMAIL_MAX = 254;

interface TeamValues {
  name: string;
  description: string;
  topics: string;
  round_robin: boolean;
  notify_email: string;
  lead_membership_id: string;
  membership_ids: string[];
}

interface TeamDialogProps {
  /** `null` creates a new team. */
  team: TeamOut | null;
  members: readonly MemberOut[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** `team-modal` "New team" (+ edit): name, description, routing topics, members, lead, mailbox, round-robin. */
export function TeamDialog({ team, members, open, onOpenChange }: TeamDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const save = useSaveTeam();
  const membersId = useId();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const schema = z.object({
    name: z.string().trim().min(1, t("team.teams.nameRequired")).max(NAME_MAX),
    description: z.string().max(DESCRIPTION_MAX),
    topics: z.string(),
    round_robin: z.boolean(),
    notify_email: z.string().trim().max(EMAIL_MAX).refine((value) => value === "" || z.string().email().safeParse(value).success, t("team.teams.notifyEmailInvalid")),
    lead_membership_id: z.string(),
    membership_ids: z.array(z.string()),
  });
  const form = useForm<TeamValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: team?.name ?? "",
      description: team?.description ?? "",
      topics: team?.routing_topics.join(", ") ?? "",
      round_robin: team?.round_robin ?? false,
      notify_email: team?.notify_email ?? "",
      lead_membership_id: team?.lead_membership_id ?? "",
      membership_ids: team?.member_ids ?? [],
    },
  });
  const selected = useWatch({ control: form.control, name: "membership_ids" });
  const lead = useWatch({ control: form.control, name: "lead_membership_id" });
  useEffect(() => {
    // the lead must be a member: taking them off the team also clears the lead
    if (lead && !selected.includes(lead)) form.setValue("lead_membership_id", "");
  }, [selected, lead, form]);
  const submit = form.handleSubmit((values) => {
    const draft = {
      name: values.name.trim(),
      description: values.description.trim() || null,
      routing_topics: parseTopics(values.topics),
      round_robin: values.round_robin,
      notify_email: values.notify_email.trim() || null,
      lead_membership_id: values.lead_membership_id || null,
      membership_ids: values.membership_ids,
    };
    save.mutate(
      { team, draft },
      {
        onSuccess: () => {
          toast.success(team ? t("team.teams.saved", { name: draft.name }) : t("team.teams.created", { name: draft.name }));
          onOpenChange(false);
        },
      },
    );
  });
  const people = members.filter((m) => m.status === "active");
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          title={team ? t("team.teams.editTeam", { name: team.name }) : t("team.teams.newTeam")}
          closeLabel={t("common.close")}
          className="max-w-md"
          footer={
            <>
              {team ? (
                <Button variant="ghost" className="mr-auto text-danger" onClick={() => { setConfirmingDelete(true); }}>
                  <Icon name="trash" />
                  {t("team.teams.delete")}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
                {t("team.cancel")}
              </Button>
              <Button type="submit" form="team-form" loading={save.isPending}>
                {team ? t("team.teams.save") : t("team.teams.create")}
              </Button>
            </>
          }
        >
          <form id="team-form" noValidate className="space-y-4" onSubmit={(event) => void submit(event)}>
            {save.error ? <FormAlert title={message(save.error)} /> : null}
            <Field label={t("team.teams.name")} error={form.formState.errors.name?.message}>
              <Input maxLength={NAME_MAX} placeholder={t("team.teams.namePlaceholder")} {...form.register("name")} />
            </Field>
            <Field label={`${t("team.teams.description")} ${t("common.optional")}`}>
              <Textarea rows={2} maxLength={DESCRIPTION_MAX} placeholder={t("team.teams.descriptionPlaceholder")} {...form.register("description")} />
            </Field>
            <Field label={t("team.teams.topics")} hint={t("team.teams.topicsHint")}>
              <Input placeholder={t("team.teams.topicsPlaceholder")} {...form.register("topics")} />
            </Field>
            <div>
              <label className="label" htmlFor={membersId}>
                {t("team.teams.members")}
              </label>
              <Controller
                control={form.control}
                name="membership_ids"
                render={({ field }) => (
                  <MultiSelect
                    id={membersId}
                    options={people.map((m) => ({ value: m.id, label: m.name, description: m.email }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    labels={{ placeholder: t("team.teams.membersPlaceholder"), search: t("team.teams.membersSearch"), empty: t("team.teams.membersEmpty") }}
                    summary={(count) => t("team.teams.membersSelected", { count })}
                  />
                )}
              />
            </div>
            <Field label={`${t("team.teams.lead")} ${t("common.optional")}`} hint={t("team.teams.leadHint")}>
              <Select disabled={selected.length === 0} {...form.register("lead_membership_id")}>
                <option value="">{t("team.teams.noLead")}</option>
                {people.filter((m) => selected.includes(m.id)).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={`${t("team.teams.notifyEmail")} ${t("common.optional")}`} hint={t("team.teams.notifyEmailHint")} error={form.formState.errors.notify_email?.message}>
              <Input type="email" maxLength={EMAIL_MAX} autoComplete="off" {...form.register("notify_email")} />
            </Field>
            <Controller
              control={form.control}
              name="round_robin"
              render={({ field }) => (
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold" id="round-robin-label">
                      {t("team.teams.roundRobin")}
                    </div>
                    <div className="text-xs text-fg-muted">{t("team.teams.roundRobinHint")}</div>
                  </div>
                  <Switch aria-labelledby="round-robin-label" checked={field.value} onCheckedChange={field.onChange} />
                </div>
              )}
            />
          </form>
        </DialogContent>
      </Dialog>
      {team ? (
        <DeleteTeamDialog
          team={team}
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          onDeleted={() => { onOpenChange(false); }}
        />
      ) : null}
    </>
  );
}
